import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';

const FeaturesGrid = () => {
  const features = [
    {
      icon: "Zap",
      title: "Fast POS",
      description: "Lightning-fast checkout process with barcode scanning and quick product search for efficient customer service",
      color: "primary"
    },
    {
      icon: "Package",
      title: "Smart Inventory",
      description: "Real-time stock tracking with low stock alerts, automatic reorder points, and multi-location management",
      color: "secondary"
    },
    {
      icon: "Scan",
      title: "Barcode Ready",
      description: "Built-in barcode scanner support with automatic product lookup and instant price updates",
      color: "accent"
    },
    {
      icon: "FileText",
      title: "Comprehensive Reports",
      description: "Detailed sales analytics, inventory reports, and business insights to drive informed decisions",
      color: "primary"
    },
    {
      icon: "CreditCard",
      title: "Payment Integration",
      description: "Accept multiple payment methods including cash, cards, and mobile money with seamless integration",
      color: "secondary"
    },
    {
      icon: "Heart",
      title: "Customer Loyalty",
      description: "Build customer relationships with loyalty programs, purchase history tracking, and personalized offers",
      color: "accent"
    }
  ];

  return (
    <section className="py-8 md:py-12 lg:py-16 bg-background">
      <div className="container mx-auto px-6 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12 md:mb-16"
        >
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-4">
            Everything You Need to Succeed
          </h2>
          <p className="font-body text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto">
            Powerful features that help merchants sell in-person and online - built for speed and reliability.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {features?.map((feature, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              whileHover={{ y: -8 }}
              className="group bg-card rounded-xl p-6 md:p-8 border border-border shadow-purple hover:shadow-purple-lg transition-smooth cursor-pointer"
            >
              <div className={`flex h-14 w-14 md:h-16 md:w-16 items-center justify-center rounded-xl bg-${feature?.color}/10 mb-6 group-hover:scale-110 transition-smooth`}>
                <Icon name={feature?.icon} size={28} color={`var(--color-${feature?.color})`} strokeWidth={2} />
              </div>
              
              <h3 className="font-heading text-xl md:text-2xl font-bold text-foreground mb-3">
                {feature?.title}
              </h3>
              
              <p className="font-body text-base text-muted-foreground leading-relaxed">
                {feature?.description}
              </p>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="text-center mt-8 md:mt-10"
        >
          <Link to="/features">
            <button className="inline-flex items-center gap-2 px-6 py-3 font-body text-base font-medium text-primary border-2 border-primary rounded-lg hover:bg-primary hover:text-primary-foreground transition-smooth">
              Explore All Features
              <Icon name="ArrowRight" size={20} color="currentColor" strokeWidth={2} />
            </button>
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

export default FeaturesGrid;